import { Component, computed, inject } from '@angular/core';
import { TranslatePipe } from '@/app/layout/pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CreateDocumentDialogComponent } from './create-document-dialog/create-document-dialog.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DocumentService } from '../../core/service/document.service';
import { DocumentTypeEnum } from '../../core/models/enums/documentType.enum';
import { ExternalApplicationsUtils } from '../../core/utils/externalApplications.utils';
import { ListExternalApplicationsEnum } from '../../core/models/enums/listExternalApplications.enum';
import { TenantService, UserService } from '../../core';
import { ToastModule } from 'primeng/toast';
import { EntrepriseOptionService } from '../../core/service/entrepriseOption.service';
import { EntrepriseOptionsEnum } from '../../core/models/enums/entrepriseOption.enum';
import { DialogModule } from 'primeng/dialog';
import { InputOtpModule } from 'primeng/inputotp';
import { FormsModule } from '@angular/forms';
import { Message } from 'primeng/message';

@Component({
  selector: 'app-documents',
  imports: [
    TranslatePipe,
    CommonModule,
    ButtonModule,
    TooltipModule,
    CreateDocumentDialogComponent,
    ConfirmDialog,
    ToastModule,
    DialogModule,
    InputOtpModule,
    FormsModule,
    Message,
  ],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.scss',
  providers: [ConfirmationService, MessageService],
})
export class DocumentsComponent {
  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private externalApplicationsUtils: ExternalApplicationsUtils
  ) {}
  displayModal: boolean = false;
  displayOtpModal: boolean = false;
  otpCode: string = '';
  documentSelectedId: string | null = null;
  documentTel: string = '';
  errorMessage: string | null = null;

  #tenantService = inject(TenantService);
  #userService = inject(UserService);
  #documentService = inject(DocumentService);
  #entrepriseOptionsService = inject(EntrepriseOptionService);

  #documentListQuery = this.#documentService.getListDocuments();
  #documentList = this.#documentListQuery.data();
  #userInfosQuery = this.#userService.getUserInfos();
  #entrepriseOptionsQuery =
    this.#entrepriseOptionsService.listEntrepriseOptions();

  isLoading = computed(() => this.#documentListQuery.isLoading());
  documentsRecus = computed(() =>
    this.#documentListQuery
      .data()
      ?.filter((x) => x.type == DocumentTypeEnum.Incoming)
      .map((doc) => ({
        ...doc,
        externalAppDescription: this.externalApplicationsUtils.getDisplayValue(
          doc.externalSystem
        ).description,
        externalAppTitle: this.externalApplicationsUtils.getDisplayValue(
          doc.externalSystem
        ).name,
      }))
  );
  documentsEnvoyes = computed(() =>
    this.#documentListQuery
      .data()
      ?.filter((x) => x.type == DocumentTypeEnum.Outgoing)
      .map((doc) => ({
        ...doc,
        externalAppDescription: this.externalApplicationsUtils.getDisplayValue(
          doc.externalSystem
        ).description,
        externalAppTitle: this.externalApplicationsUtils.getDisplayValue(
          doc.externalSystem
        ).name,
      }))
  );

  linkApplicationEnabled = computed(() => {
    let data = this.#entrepriseOptionsQuery.data() ?? [];
    let x =
      data.find(
        (x: any) =>
          x.key.toLowerCase() ==
          EntrepriseOptionsEnum.LinkApplicationEnabled.toLowerCase()
      )?.value ?? false;
    return x.toString().toLowerCase() === 'true';
  });

  hasNoExternalLinks = computed(
    () => (this.#userInfosQuery.data()?.externalLinks?.length ?? 0) === 0
  );

  // externalApplications = Object.values(ListExternalApplicationsEnum).map((app) => ({
  //   key: app,
  //   display: this.externalApplicationsUtils.getDisplayValue(app),
  // }));

  showModal() {
    this.displayModal = true;
  }

  hideModal() {
    this.displayModal = false;
  }

  validatorVerificationCode(): boolean {
    const codeRegex = /^\d{8}$/;
    return codeRegex.test(this.otpCode);
  }

  handlePaste(event: ClipboardEvent) {
    const pastedData = event.clipboardData?.getData('text') || '';
    if (/^\d{8}$/.test(pastedData)) {
      this.otpCode = pastedData;
      this.validatorVerificationCode();
    }
    event.preventDefault();
  }

  askDownloadCode(documentId: string) {
    const doc = this.#documentListQuery
      .data()
      ?.find((d: any) => d.id === documentId);
    this.documentTel = doc?.telBeneficiaire || '';
    this.#documentService.requestDownloadCode.mutate(
      { documentId },
      {
        onSuccess: () => {
          this.documentSelectedId = documentId;
          this.otpCode = '';
          this.errorMessage = null;
          this.displayOtpModal = true;
        },
        onError: () => {
          this.messageService.add({
            key: 'global',
            severity: 'error',
            summary: 'Erreur',
            detail:
              'Une erreur est survenue lors de la demande de code de téléchargement. Veuillez réessayer, si cela persiste, merci de contacter notre secrétariat.',
          });
        },
      }
    );
  }

  onOtpCodeChange(event: any) {
    const code = typeof event === 'string' ? event : event?.target?.value ?? '';
    this.otpCode = code;
    if (!this.validatorVerificationCode()) {
      this.errorMessage = 'Le code doit comporter 8 chiffres.';
    } else {
      this.errorMessage = null;
    }
  }

  downloadDocument(tokenData: { token: string; expires: string }) {
    if (!this.documentSelectedId) {
      this.errorMessage = 'Aucun document sélectionné.';
      return;
    }

    this.#documentService.askDownload.mutate(
      {
        documentId: this.documentSelectedId,
        token: tokenData.token,
      },
      {
        onSuccess: (result: any) => {
          // Succès, lancer le téléchargement
          const baseUrl = this.#tenantService.getBffBaseUrl();
          const downloadUrl = `${baseUrl}/api/core/documents/download?key=${result.key}&token=${result.token}`;
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          if (!isIOS) {
            const win = window.open('', '_blank');
            if (win) {
              win.location.href = downloadUrl;
              setTimeout(() => {
                win.close();
              }, 1000);
            } else {
              window.location.href = downloadUrl;
            }
          } else {
            window.location.href = downloadUrl;
          }
          this.messageService.add({
            key: 'global',
            severity: 'success',
            summary: 'Téléchargement démarré',
            detail: 'Votre document est en cours de téléchargement.',
          });
        },
        onError: (error: any) => {
          if (
            error &&
            error.error &&
            error.error.detail === 'Le jeton de téléchargement a expiré.'
          ) {
            this.messageService.add({
              key: 'global',
              severity: 'error',
              summary: 'Erreur',
              detail:
                'Le jeton de téléchargement a expiré. Veuillez saisir un nouveau code.',
            });

            localStorage.removeItem('documentTokenDownload');

            this.askDownload(this.documentSelectedId!);
          } else {
            this.errorMessage = 'Erreur lors de la récupération du document.';
          }
        },
      }
    );
    return;
  }

  onCreated(event: any) {
    this.displayModal = false;
  }

  onCancelled() {
    this.displayModal = false;
  }

  confirmDelete(id: string) {
    let doc = this.documentsEnvoyes()?.find((x) => x.id == id);
    if (doc?.retentionRequired)
      this.messageService.add({
        key: 'global',
        severity: 'warn',
        summary: 'Attention',
        detail:
          'Ce document est soumis à une obligation de conservation, vous ne pouvez pas le supprimer.',
      });
    else
      this.confirmationService.confirm({
        message: 'Voulez-vous supprimer ce document ?',
        header: 'Attention',
        icon: 'pi pi-info-circle',
        rejectLabel: 'Annuler',
        rejectButtonProps: {
          label: 'Annuler',
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: 'Supprimer',
          severity: 'danger',
        },
        accept: () => {
          this.#documentService.deleteDocument.mutate(
            { documentId: id },
            {
              onSuccess: () => {
                this.messageService.add({
                  key: 'global',
                  severity: 'success',
                  summary: 'Supprimé',
                  detail: 'Document supprimé !',
                });
              },
              onError: () => {
                this.messageService.add({
                  key: 'global',
                  severity: 'error',
                  summary: 'Erreur',
                  detail: 'Erreur lors de la suppression du document.',
                });
              },
            }
          );
        },
      });
  }

  askDownload(documentId: string) {
    this.documentSelectedId = documentId;

    const tokenDataRaw = localStorage.getItem('documentTokenDownload');
    const tokenData = tokenDataRaw ? JSON.parse(tokenDataRaw) : null;

    if (tokenData && tokenData.token && tokenData.expires) {
      this.downloadDocument(tokenData);
      return;
    }

    this.displayOtpModal = true;
    this.errorMessage = '';
    this.otpCode = '';

    this.askDownloadCode(this.documentSelectedId);
  }

  validateAndStoreDownloadToken() {
    if (!this.documentSelectedId || !this.validatorVerificationCode()) {
      this.errorMessage = 'Veuillez saisir un code valide.';
      return;
    }
    this.#documentService.validateDownloadCode.mutate(
      {
        documentId: this.documentSelectedId,
        verificationCode: this.otpCode,
      },
      {
        onSuccess: (result: any) => {
          try {
            const data =
              typeof result === 'string' ? JSON.parse(result) : result;
            localStorage.setItem(
              'documentTokenDownload',
              JSON.stringify({ token: data.token, expires: data.expiration })
            );
            this.errorMessage = null;
            this.displayOtpModal = false;
            this.askDownload(this.documentSelectedId!);
          } catch {
            this.errorMessage = 'Erreur lors de la récupération du token.';
          }
        },
        onError: (error: any) => {
          if (error && error.error && error.error.detail) {
            this.errorMessage = error.error.detail;
          } else {
            this.errorMessage = 'Erreur lors de la validation du code.';
          }
        },
      }
    );
  }
}
